import { getCSRFToken } from "./htmx-helpers";

/**
 * Alpine component factory for the Reassign (PMS apartment binding) confirmation
 * modal rendered by templates/components/booking_widget_form.templ.
 *
 * Registered at bundle-load time in studio-bundle.ts — NOT via an inline
 * `alpine:init` listener. The booking-widget section is lazy-loaded via HTMX
 * (website-detail page renders it as ?partial=content&section=booking), which
 * injects the `x-data="reassignConfirm"` markup AFTER Alpine has already
 * started and `alpine:init` has fired. A component registered inside an
 * `alpine:init` listener that ships with the swapped-in fragment never runs,
 * so Alpine can't resolve the component and throws "reassignConfirm is not
 * defined". Registering here (before Alpine.start()) makes the component
 * available regardless of when the section is swapped in.
 *
 * The three localized confirm-message templates arrive as data-* on $root
 * (`data-tpl-unbound` / `data-tpl-active` / `data-tpl-draft`) with %APT% /
 * %HOLDER% placeholders so go-i18n interpolation stays server-side; this
 * component only substitutes the chosen apartment/holder names client-side.
 * The per-option data-holder-kind (none|active|draft) from the picker fragment
 * selects which message shows. The row's Reassign button stashes the target
 * org slug + property id on window.__reassign* before dispatching `reassign-open`,
 * whose detail also carries the row's open-bookings warning (data-open-bookings-msg).
 */
export function reassignConfirm() {
  return {
    chosen: false,
    mode: "", // 'assign' | 'unbind'
    isBound: false, // does the reassigning row currently have a binding?
    extId: "",
    name: "",
    holderName: "",
    holderKind: "",
    connId: "",
    unboundMsg: "",
    activeMsg: "",
    draftMsg: "",
    // Server-translated warning about the row's open bookings (empty = none):
    // they stay on the current apartment when the binding changes.
    openBookingsMsg: "",
    // Fired by the row's Reassign button (before the picker loads) so the
    // modal opens fresh and knows whether to offer "remove link".
    onOpen(detail: { isBound?: boolean; openBookingsMsg?: string } | undefined) {
      this.chosen = false;
      this.mode = "";
      this.isBound = !!(detail && detail.isBound);
      this.openBookingsMsg = (detail && detail.openBookingsMsg) || "";
      this.extId = "";
      this.name = "";
      this.holderName = "";
      this.holderKind = "";
      this.connId = "";
    },
    // A .reassign-option inside #reassign-picker-body was clicked -> assign.
    onOptionClick(e: Event) {
      const btn = (e.target as HTMLElement).closest(".reassign-option") as HTMLElement | null;
      if (!btn) return;
      const ul = btn.closest("ul[data-connection-id]") as HTMLElement | null;
      this.mode = "assign";
      this.extId = btn.dataset.extId || "";
      this.name = btn.dataset.name || "";
      this.holderName = btn.dataset.holderName || "";
      this.holderKind = btn.dataset.holderKind || "none";
      this.connId = ul ? ul.dataset.connectionId || "" : "";
      const root = (this as any).$root as HTMLElement;
      const sub = (tpl: string | undefined) =>
        (tpl || "").replaceAll("%APT%", this.name).replaceAll("%HOLDER%", this.holderName);
      this.unboundMsg = sub(root.dataset.tplUnbound);
      this.activeMsg = sub(root.dataset.tplActive);
      this.draftMsg = sub(root.dataset.tplDraft);
      this.chosen = true;
    },
    // "Remove link" in the current-binding block -> unbind (no apartment,
    // no connection needed; the server clears the link).
    chooseUnbind() {
      this.mode = "unbind";
      this.extId = "";
      this.connId = "";
      this.chosen = true;
    },
    submit() {
      if (!this.chosen) return;
      const orgSlug = (window as any).__reassignOrgSlug;
      const propertyID = (window as any).__reassignRowPropertyID;
      const htmx = (window as any).htmx;
      htmx.ajax("POST", `/organizations/${orgSlug}/api/properties/${propertyID}/pms-binding`, {
        values: { apartment_external_id: this.extId, connection_id: this.connId },
        headers: { "X-CSRF-Token": getCSRFToken() },
      });
      (document.getElementById("reassign_confirm_modal") as HTMLDialogElement).close();
      // Reset so re-opening the modal for another row starts blank.
      this.chosen = false;
      this.mode = "";
    },
  };
}
